import type { ReactNode } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';

import { Dialog, DialogButton, DialogContentText } from '@/components/Dialog';
import useDataHubExport from '@/features/DataHub/modules/Export/hooks/useDataHubExport';
import useUserDetails from '@/hooks/useUserDetails';

import {
  ExportMetaProvider,
  ExportPreviewProvider,
  ExportRequestProvider,
  ExportSettingsProvider,
  useExportRequest
} from './exportContexts';

const ExportModalsWrapper = () => {
  const { exportStatus, resetExportStatus } = useExportRequest();

  const isLoading = exportStatus === 'loading';
  const isQueued = exportStatus === 'queued';

  const { userEmail } = useUserDetails();

  return (
    <>
      <Dialog
        open={isLoading}
        onClose={() => {}}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%', py: '16px' }}>
          <CircularProgress size={24} />
          <DialogContentText>Your request is being processed...</DialogContentText>
        </Box>
      </Dialog>

      <Dialog
        buttons={<DialogButton onClick={resetExportStatus}>OK</DialogButton>}
        open={isQueued}
        title="We're on it."
        onClose={resetExportStatus}
      >
        <DialogContentText>
          You will get an email shortly to{' '}
          <Typography
            sx={{
              textDecoration: 'underline',
              display: 'inline'
            }}
          >
            {userEmail}
          </Typography>
        </DialogContentText>
      </Dialog>
    </>
  );
};

export const ExportProvider = ({ children }: { children: ReactNode }) => {
  const { settings, preview, request, meta } = useDataHubExport();

  return (
    <ExportSettingsProvider value={settings}>
      <ExportPreviewProvider value={preview}>
        <ExportRequestProvider value={request}>
          <ExportMetaProvider value={meta}>
            {children}
            <ExportModalsWrapper />
          </ExportMetaProvider>
        </ExportRequestProvider>
      </ExportPreviewProvider>
    </ExportSettingsProvider>
  );
};
